import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Select, InputNumber, Button, Space, Row, Col, Card, Tag } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, EditOutlined, FireOutlined, PlusOutlined } from '@ant-design/icons';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { SERVER_IP } from 'assets/Config';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { useI18n } from 'hooks/useI18n';
import {
	getWorkoutGenderOptions,
	getWorkoutLevelOptions,
	getWorkoutMuscleGroupOptions,
	translateWorkoutGender,
	translateWorkoutLevel,
	translateWorkoutMuscleGroup,
} from 'helpers/workoutI18n';

import './workout-form.scss';

const { TextArea } = Input;

const getWorkoutEntity = (payload) => {
	if (!payload) {
		return null;
	}

	if (Array.isArray(payload?.data)) {
		return payload.data[0] || null;
	}

	if (payload?.data && !Array.isArray(payload.data)) {
		return payload.data;
	}

	return payload;
};

const WorkoutForm = () => {
	const { id } = useParams();
	const isEdit = Boolean(id);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutsRedux = useSelector((state) => state.workoutsRedux);
	const { t } = useI18n();
	const [form] = Form.useForm();
	const workout = useMemo(() => getWorkoutEntity(workoutsRedux?.currentWorkout), [workoutsRedux?.currentWorkout]);
	const levelValue = Form.useWatch('level', form);
	const muscleGroupValue = Form.useWatch('muscleGroup', form);
	const equipmentValue = Form.useWatch('equipment', form);
	const genderValue = Form.useWatch('gender', form);
	const muscleGroupOptions = useMemo(() => getWorkoutMuscleGroupOptions(t, 'workouts.enums.muscle_group.select_muscle_group_option'), [t]);
	const genderOptions = useMemo(() => getWorkoutGenderOptions(t, 'workouts.enums.gender.select_gender_option'), [t]);
	const levelOptions = useMemo(() => getWorkoutLevelOptions(t, 'workouts.enums.level.select_level_option'), [t]);

	useEffect(() => {
		if (isEdit) {
			dispatch(getApi('GET_WORKOUT_BY_ID', `${SERVER_IP}api/workouts/${id}`));
		}
	}, [isEdit, id, dispatch]);

	useEffect(() => {
		if (isEdit) {
			const data = workout;
			if (data) {
				form.setFieldsValue({
					name: data.name,
					muscleGroup: data.muscleGroup,
					equipment: data.equipment,
					gender: data.gender,
					level: data.level,
					defaultSets: data.defaultSets,
					defaultReps: data.defaultReps,
					defaultRestSeconds: data.defaultRestSeconds,
					videoUrl: data.videoUrl,
					imageUrl: data.imageUrl,
					instructions: data.instructions,
				});
			}
		}
	}, [isEdit, workout, form]);

	useEffect(() => {
		const apiName = isEdit ? 'EDIT_WORKOUT' : 'ADD_WORKOUT';
		if (globalRedux.apiStatus[apiName] === 'SUCCESS') {
			dispatch(resetApiStatus(apiName));
			navigate('/workouts');
		}
	}, [globalRedux.apiStatus, isEdit, dispatch, navigate]);

	const handleSubmit = (values) => {
		if (isEdit) {
			dispatch(putApi(values, 'EDIT_WORKOUT', `${SERVER_IP}api/workouts/${id}`));
		} else {
			dispatch(postApi(values, 'ADD_WORKOUT', `${SERVER_IP}api/workouts`));
		}
	};

	const loading =
		globalRedux.apiStatus.ADD_WORKOUT === 'PENDING' || globalRedux.apiStatus.EDIT_WORKOUT === 'PENDING';

	return (
		<div className="workout-form-page">
			<div className="workout-form-page__shell">
				<div className="workout-form-page__header-row">
					<Button className="workout-form-page__back-btn" icon={<ArrowLeftOutlined />} onClick={() => navigate('/workouts')}>
						{t('common.back')}
					</Button>
				</div>

				<Card className="workout-form-page__hero" bordered={false}>
					<div className="workout-form-page__hero-copy">
						<div className="workout-form-page__hero-topline">
							<span className="workout-form-page__eyebrow">{t('workouts.form.eyebrow')}</span>
							<span className="workout-form-page__hero-mode-pill">{isEdit ? t('workouts.form.update_workflow') : t('workouts.form.create_workflow')}</span>
						</div>
						<h1 className="workout-form-page__title">{isEdit ? t('workouts.form.edit_title') : t('workouts.form.add_title')}</h1>
						<p className="workout-form-page__subtitle">
							{t('workouts.form.subtitle')}
						</p>
						<div className="workout-form-page__hero-lead">
							<div className="workout-form-page__hero-lead-title">{t('workouts.form.hero_lead_title')}</div>
							<div className="workout-form-page__hero-lead-copy">
								{t('workouts.form.hero_lead_copy')}
							</div>
						</div>
						<div className="workout-form-page__hero-tags">
							<Tag className="workout-form-page__hero-tag workout-form-page__hero-tag--mode">{isEdit ? t('workouts.form.update_mode') : t('workouts.form.create_mode')}</Tag>
							{levelValue ? <Tag className="workout-form-page__hero-tag workout-form-page__hero-tag--accent">{translateWorkoutLevel(t, levelValue, levelValue)}</Tag> : null}
							{muscleGroupValue ? <Tag className="workout-form-page__hero-tag">{translateWorkoutMuscleGroup(t, muscleGroupValue, muscleGroupValue)}</Tag> : null}
							{equipmentValue ? <Tag className="workout-form-page__hero-tag">{equipmentValue}</Tag> : null}
							{genderValue ? <Tag className="workout-form-page__hero-tag workout-form-page__hero-tag--soft">{translateWorkoutGender(t, genderValue, genderValue)}</Tag> : null}
						</div>
					</div>
					<div className="workout-form-page__hero-cards">
						<div className="workout-form-page__mini-card">
							<div className="workout-form-page__mini-icon"><FireOutlined /></div>
							<div>
								<div className="workout-form-page__mini-value">{workout?.defaultSets || 0} x {workout?.defaultReps || '-'}</div>
								<div className="workout-form-page__mini-label">{t('workouts.form.current_volume')}</div>
							</div>
						</div>
						<div className="workout-form-page__mini-card">
							<div className="workout-form-page__mini-icon">{isEdit ? <EditOutlined /> : <PlusOutlined />}</div>
							<div>
								<div className="workout-form-page__mini-value">{isEdit ? t('workouts.form.editing_live_data') : t('workouts.form.new_workout_setup')}</div>
								<div className="workout-form-page__mini-label">{t('workouts.form.form_context')}</div>
							</div>
						</div>
					</div>
				</Card>

				<Card className="workout-form-page__panel" bordered={false}>
				<Form form={form} layout="vertical" onFinish={handleSubmit}>
					<Row gutter={[16, 8]}>
						<Col xs={24} sm={12}>
							<Form.Item name="name" label={t('workouts.form.fields.name')} rules={[{ required: true, message: t('workouts.form.validation.name_required') }]}>
								<Input placeholder={t('workouts.form.placeholders.name')} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item name="muscleGroup" label={t('workouts.form.fields.muscle_group')} rules={[{ required: true, message: t('workouts.form.validation.muscle_group_required') }]}>
								<Select placeholder={t('workouts.form.placeholders.select_muscle_group')} options={muscleGroupOptions.slice(1)} allowClear />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item name="equipment" label={t('workouts.form.fields.equipment')} rules={[{ required: true, message: t('workouts.form.validation.equipment_required') }]}>
								<Input placeholder={t('workouts.form.placeholders.equipment')} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item name="gender" label={t('workouts.form.fields.gender')} rules={[{ required: true, message: t('workouts.form.validation.gender_required') }]}>
								<Select placeholder={t('workouts.form.placeholders.select_gender')} options={genderOptions.slice(1)} allowClear />
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item name="level" label={t('workouts.form.fields.level')} rules={[{ required: true, message: t('workouts.form.validation.level_required') }]}>
								<Select placeholder={t('workouts.form.placeholders.select_level')} options={levelOptions.slice(1)} allowClear />
							</Form.Item>
						</Col>
						<Col xs={24} sm={5}>
							<Form.Item name="defaultSets" label={t('workouts.form.fields.default_sets')} rules={[{ required: true, message: t('workouts.form.validation.default_sets_required') }]}>
								<InputNumber min={1} max={20} style={{ width: '100%' }} placeholder={t('workouts.form.placeholders.default_sets')} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={5}>
							<Form.Item name="defaultReps" label={t('workouts.form.fields.default_reps')} rules={[{ required: true, message: t('workouts.form.validation.default_reps_required') }]}>
								<Input placeholder={t('workouts.form.placeholders.default_reps')} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={6}>
							<Form.Item name="defaultRestSeconds" label={t('workouts.form.fields.rest_seconds')} rules={[{ required: true, message: t('workouts.form.validation.rest_seconds_required') }]}>
								<InputNumber min={0} step={15} style={{ width: '100%' }} placeholder={t('workouts.form.placeholders.rest_seconds')} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item name="videoUrl" label={t('workouts.form.fields.video_url')}>
								<Input placeholder={t('workouts.form.placeholders.video_url')} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item name="imageUrl" label={t('workouts.form.fields.image_url')}>
								<Input placeholder={t('workouts.form.placeholders.image_url')} />
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Form.Item name="instructions" label={t('workouts.form.fields.instructions')}>
								<TextArea rows={4} placeholder={t('workouts.form.placeholders.instructions')} />
							</Form.Item>
						</Col>
					</Row>
					<div className="workout-form-page__actions">
						<Space size={12} wrap>
							<Button className="workout-form-page__submit-btn" type="primary" htmlType="submit" loading={loading} icon={<CheckCircleOutlined />}>
							{isEdit ? t('workouts.form.update_exercise') : t('workouts.form.add_exercise')}
						</Button>
							<Button className="workout-form-page__cancel-btn" onClick={() => navigate('/workouts')}>{t('common.cancel')}</Button>
						</Space>
					</div>
				</Form>
				</Card>
			</div>
		</div>
	);
};

export default WorkoutForm;
